import React, { useState } from "react";
import { SectionBadge } from "./Comparison";
import { Star, ArrowRight, Trophy } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Link } from "react-router-dom";

const APPLICANTS = [
  {
    company: "Nova Fabrications Ltd",
    focus: "Manufacturing · Birmingham · £2.1M turnover",
    quote: "Bank said 6 weeks. SwiftCash had signed terms in 48 hours. We funded the new CNC line before Q4 orders hit the floor — biggest quarter we've ever run.",
    author: "Sarah Malik, Managing Director",
    amount: "£350k",
    product: "Asset Finance",
    speed: "48 hrs",
    story: [
      "Nova Fabrications is a family-owned CNC engineering business based in Birmingham, turning over £2.1M and supplying tier-2 automotive and aerospace parts across the Midlands. In summer 2025, Sarah Malik — managing director and second-generation owner — landed a contract that would double their annual output, but only if they could add a second five-axis CNC line before September.",
      "Their high-street bank quoted six weeks for a decision on a £350k asset finance facility. Sarah tried three specialist lenders directly; two came back with rates 3% above where she'd modelled, one wanted an all-monies debenture across three subsidiaries.",
      "\"A friend on LinkedIn mentioned SwiftCash. I filled the form in on my phone in the car park. By that afternoon we had three lenders bidding. By the next morning I had signed indicative terms at 7.9% APR, on the machine only — no debenture, no PGs beyond me and my brother.\"",
      "The full facility drew down 11 days after the initial application. The CNC line was commissioned on schedule and the automotive contract went live in early October. Nova's Q4 revenue was up 68% year on year.",
      "\"For any business owner who's been through the traditional bank process,\" Sarah says, \"SwiftCash is a completely different experience. Zero admin theatre. Real lenders competing for our deal. And they didn't take a single penny out of me — the lender pays them. It's the model banking should have moved to a decade ago.\"",
    ],
  },
  {
    company: "Harbour & Hound",
    focus: "Hospitality · 4-site pub group · Bristol",
    quote: "Second freehold acquisition. Bank wouldn't touch a group under 5 years. SwiftCash brought us a specialist hospitality lender at 6.2% — deal done, keys collected, doors open six weeks later.",
    author: "James Cartwright, Co-founder",
    amount: "£1.8M",
    product: "Commercial Mortgage",
    speed: "5 weeks",
    story: [
      "Harbour & Hound run a growing pub group across Bristol and the West Country. Their fourth site — a freehold pub near Clifton — came on the market at £2.4M in early 2025. James Cartwright and his co-founder had £600k in cash and needed £1.8M in commercial mortgage finance to complete.",
      "Every high-street lender declined on trading history: the group had been operating under its current structure for 3 years, and the underwriting appetite for hospitality below 5 years' trading was cautious across the board.",
      "\"The estate agent recommended SwiftCash. I was skeptical — I'd never used a broker for property finance before. But the platform is dead simple. Amount, term, purpose, some financials, done. Within 24 hours we had two specialist hospitality lenders in the mix.\"",
      "One of those lenders — a north-west based commercial property specialist — priced it at 6.2% APR fixed for 5 years, on a 25-year amortisation with a 15% deposit. Valuation and legals took 4 weeks. The Cartwright group completed the purchase 5 weeks after the initial application.",
      "\"We've since done another £900k refinance through the same platform. The point is: SwiftCash gets you to lenders you'd never find alone. And because it's competitive from day one, the pricing is sharper than anything a direct approach would produce.\"",
    ],
  },
  {
    company: "Bramble Growth Studios",
    focus: "Tech · Software agency · £4.8M turnover",
    quote: "Cash flow crunch on a big client extension. Applied Tuesday, £180k invoice finance line live on Friday. No PG, no debenture — just the invoices. Saved payroll and the client relationship.",
    author: "Priya Kapoor, CEO",
    amount: "£180k",
    product: "Invoice Finance",
    speed: "3 days",
    story: [
      "Bramble Growth Studios is a London-based software agency turning over £4.8M with 34 permanent staff. Their largest client — a FTSE-listed insurer — asked to extend payment terms from 30 to 90 days on active projects worth £480k. Bramble's CEO Priya Kapoor had two weeks before it hit payroll.",
      "\"We could have said no to the client, but they're 22% of our revenue and we've worked with them since 2019. I needed a working capital line, fast, without renegotiating every existing lending covenant we have with our bank.\"",
      "Priya submitted a SwiftCash application on the Tuesday, uploaded 12 months of bank statements and the last two sets of filed accounts on the review screen, and had indicative terms from two lenders by Wednesday afternoon. She picked a selective invoice finance facility from a specialist funder — 90% advance rate, 2.4% monthly fee, purely secured against the underlying receivables.",
      "The facility drew down Friday, four days after she'd filled in the form. Payroll ran on schedule, the client relationship stayed intact, and Bramble had a rolling working capital line they could scale up or down as receivables ebbed and flowed.",
      "\"The killer thing for me was: no personal guarantee. No debenture. No touching my existing bank facility. It's exactly the kind of surgical funding tool a growing agency needs and can never get from the high street. If I'd known about SwiftCash a year ago, we'd have grown faster.\"",
    ],
  },
];

export default function ApplicantTestimonials() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="relative bg-paper-warm py-24 md:py-32" data-testid="applicant-testimonials">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <SectionBadge>Funded businesses</SectionBadge>
          <h2 className="font-display font-extrabold text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.035em] text-navy mt-5">
            Real businesses. <br />
            <span className="text-emerald">Real outcomes.</span>
          </h2>
          <p className="mt-6 text-[16px] text-navy/65 font-medium max-w-xl mx-auto leading-[1.55]">
            The stories behind the deals — how UK founders and MDs closed the funding round, bought the freehold, or hit the growth number.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 items-stretch">
          {APPLICANTS.map((a, i) => (
            <button key={i} data-testid={`applicant-testimonial-${i + 1}`} onClick={() => setOpenIdx(i)}
              className="text-left rounded-[20px] bg-white border border-line p-7 md:p-8 hover:border-emerald/40 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full group">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-amber text-amber" />)}
                </div>
                <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] font-extrabold text-navy/40 group-hover:text-emerald-3 transition-colors">
                  Read story <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} />
                </span>
              </div>
              <p className="text-[15px] leading-[1.6] text-navy/80 font-medium mb-8 flex-1 min-h-[190px]">
                &ldquo;{a.quote}&rdquo;
              </p>
              <div className="pt-5 border-t border-line">
                <div className="font-display font-extrabold text-[16px] text-navy tracking-[-0.015em] mb-1 min-h-[24px]">{a.company}</div>
                <div className="text-[12px] text-navy/55 font-semibold mb-4 min-h-[36px] leading-[1.5]">{a.focus}</div>
                <div className="grid grid-cols-3 gap-2">
                  <MicroStat label="Amount" value={a.amount} />
                  <MicroStat label="Product" value={a.product} />
                  <MicroStat label="Time to fund" value={a.speed} />
                </div>
                <div className="mt-4 text-[11.5px] text-navy/50 font-mono uppercase tracking-[0.1em] font-bold">{a.author}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={openIdx !== null} onOpenChange={(o) => !o && setOpenIdx(null)}>
        <DialogContent className="max-w-3xl bg-white border-line rounded-[24px] p-0 overflow-hidden max-h-[88vh] flex flex-col" data-testid="applicant-story-modal">
          {openIdx !== null && (
            <>
              <div className="bg-navy text-white p-7 md:p-9 relative overflow-hidden shrink-0">
                <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-4">
                    <Trophy className="h-3 w-3" strokeWidth={2.6} /> Funded case study
                  </div>
                  <h2 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] leading-[1.1] text-white mb-2">
                    {APPLICANTS[openIdx].company}
                  </h2>
                  <div className="text-[13px] text-white/65 font-semibold">{APPLICANTS[openIdx].focus}</div>
                  <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 max-w-md">
                    <ModalStat label="Amount" value={APPLICANTS[openIdx].amount} />
                    <ModalStat label="Product" value={APPLICANTS[openIdx].product} />
                    <ModalStat label="Time to fund" value={APPLICANTS[openIdx].speed} />
                  </div>
                </div>
              </div>
              <div className="p-7 md:p-9 overflow-y-auto">
                <div className="rounded-xl bg-emerald-soft/60 border border-emerald/20 p-5 mb-7">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-amber text-amber" />)}
                  </div>
                  <p className="text-[15.5px] leading-[1.6] text-navy font-semibold italic">
                    &ldquo;{APPLICANTS[openIdx].quote}&rdquo;
                  </p>
                  <div className="mt-3 text-[11.5px] text-navy/55 font-mono uppercase tracking-[0.1em] font-bold">
                    {APPLICANTS[openIdx].author}
                  </div>
                </div>
                <div className="space-y-5">
                  {APPLICANTS[openIdx].story.map((para, i) => (
                    <p key={i} className="text-[15px] leading-[1.7] text-navy/80 font-medium">{para}</p>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-line flex flex-col sm:flex-row gap-3">
                  <Link to="/apply" onClick={() => setOpenIdx(null)} data-testid="applicant-story-apply"
                    className="btn-shine group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald flex-1">
                    Get my tailored quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
                  </Link>
                  <button onClick={() => setOpenIdx(null)}
                    className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-line text-navy/70 font-bold hover:bg-paper-warm text-[13.5px]">
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function MicroStat({ label, value }) {
  return (
    <div className="rounded-lg bg-emerald-soft/60 px-2 py-2.5 text-center flex flex-col justify-center min-h-[64px] min-w-0 overflow-hidden">
      <div
        className="font-display font-extrabold text-[12px] sm:text-[12.5px] text-navy tracking-[-0.01em] leading-[1.15] break-words hyphens-auto"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-navy/50 mt-1.5 font-bold">{label}</div>
    </div>
  );
}
function ModalStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-center min-w-0 overflow-hidden">
      <div
        className="font-display font-extrabold text-[14px] sm:text-[15px] tracking-[-0.02em] leading-[1.15] break-words hyphens-auto text-white"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-white/55 mt-1.5 font-bold">{label}</div>
    </div>
  );
}
